/* eslint-disable jsx-a11y/click-events-have-key-events */
import { memo } from 'react';
import PropTypes from 'prop-types';
import { isNil } from 'lodash';
import { useTranslation } from 'react-i18next';
// Import Components
import { SkeletonLoader } from '@geneui/components';
import SectionWithTitle from 'components/common/SectionWithTitle';
// Import Hooks
import { usePartnerDateFormats } from 'hooks/UsePartnerDateFormats';
// Import Services
import { Helpers } from 'services';
// Import Constants
import { l, StateModesLabels, CustomerJourneyStatusLabels, TimeZonePack } from 'constants/common';

const { customMomentWithoutTimezoneConversion } = Helpers;

const CustomerJourneyGeneralDetails = ({
    schedule,
    mode,
    status,
    user,
    createdDate,
    lastModifier,
    lastModifiedDate,
    customFields,
}) => {
    const { t } = useTranslation();
    const { partnerDateTimeWithoutSecondsFormat } = usePartnerDateFormats();

    const getContent = (data) => {
        return isNil(data)
            ? t(l.NA)
            : customMomentWithoutTimezoneConversion(data).format(partnerDateTimeWithoutSecondsFormat);
    };

    return (
        <SectionWithTitle
            title={t(l.GeneralDetails)}
            className="general-details-section"
            contentClassName="general-details-section-content"
        >
            <div className="general-details-column-info">
                <p className="g-details-title">{t(l.AutoStart)}</p>
                <SkeletonLoader isBusy={isNil(status)}>
                    {!isNil(status) && <p className="g-details-subtitle">{getContent(schedule?.StartDate)}</p>}
                </SkeletonLoader>
            </div>
            <div className="general-details-column-info">
                <p className="g-details-title">{t(l.User)}</p>
                <SkeletonLoader isBusy={isNil(user)}>
                    {!isNil(user) && <p className="g-details-subtitle">{user}</p>}
                </SkeletonLoader>
            </div>
            <div className="general-details-column-info">
                <p className="g-details-title">{t(l.AutoEnd)}</p>
                <SkeletonLoader isBusy={isNil(status)}>
                    {!isNil(status) && <p className="g-details-subtitle">{getContent(schedule?.EndDate)}</p>}
                </SkeletonLoader>
            </div>
            <div className="general-details-column-info">
                <p className="g-details-title">{t(l.CreatedDate)}</p>
                <SkeletonLoader isBusy={isNil(createdDate)}>
                    {!isNil(createdDate) && <p className="g-details-subtitle">{getContent(createdDate)}</p>}
                </SkeletonLoader>
            </div>
            <div className="general-details-column-info">
                <p className="g-details-title">{t(l.TimeZone)}</p>
                <SkeletonLoader isBusy={isNil(status)}>
                    {!isNil(status) && (
                        <p className="g-details-subtitle">
                            {isNil(schedule?.TimeZone) ? t(l.NA) : TimeZonePack[schedule?.TimeZone]}
                        </p>
                    )}
                </SkeletonLoader>
            </div>
            <div className="general-details-column-info">
                <p className="g-details-title">{t(l.LastModified)}</p>
                <SkeletonLoader isBusy={isNil(lastModifiedDate)}>
                    {!isNil(lastModifiedDate) && <p className="g-details-subtitle">{getContent(lastModifiedDate)}</p>}
                </SkeletonLoader>
            </div>
            <div className="general-details-column-info">
                <p className="g-details-title">{t(l.Mode)}</p>
                <SkeletonLoader isBusy={isNil(mode)}>
                    {!isNil(mode) && <p className="g-details-subtitle">{StateModesLabels[mode]}</p>}
                </SkeletonLoader>
            </div>
            <div className="general-details-column-info">
                <p className="g-details-title">{t(l.LastModifier)}</p>
                <SkeletonLoader isBusy={isNil(lastModifier)}>
                    {!isNil(lastModifier) && (
                        <p className="g-details-subtitle">{isNil(lastModifier) ? t(l.NA) : lastModifier}</p>
                    )}
                </SkeletonLoader>
            </div>
            <div className="general-details-column-info">
                <p className="g-details-title">{t(l.Status)}</p>
                <SkeletonLoader isBusy={isNil(status)}>
                    {!isNil(status) && <p className="g-details-subtitle">{CustomerJourneyStatusLabels[status]}</p>}
                </SkeletonLoader>
            </div>
            {customFields.map((field) => (
                <div className="general-details-column-info" key={field.CustomFieldId}>
                    <p className="g-details-title">{field.ViewData?.Name}</p>
                    <p className="g-details-subtitle">{field.ViewData?.Value}</p>
                </div>
            ))}
        </SectionWithTitle>
    );
};

CustomerJourneyGeneralDetails.propTypes = {
    schedule: PropTypes.object.isRequired,
    mode: PropTypes.number.isRequired,
    status: PropTypes.number.isRequired,
    user: PropTypes.string.isRequired,
    createdDate: PropTypes.string.isRequired,
    lastModifier: PropTypes.string.isRequired,
    lastModifiedDate: PropTypes.string.isRequired,
    customFields: PropTypes.array,
};

CustomerJourneyGeneralDetails.defaultProps = {
    customFields: [],
};

export default memo(CustomerJourneyGeneralDetails);
